import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

// Referral-agreement gate: fetched fresh every time the dialog opens (the
// version can change out from under a long-lived session). Acceptance is
// idempotent server-side, so re-accepting the same version is harmless.
export default function TermsDialog({ open, onOpenChange, onAccepted }) {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/pharos/terms")
      .then((res) => setTerms(res.data))
      .catch((err) => toast.error(getErrorMessage(err, "Failed to load terms")))
      .finally(() => setLoading(false));
  }, [open]);

  async function handleAccept() {
    if (!terms?.version) return;
    setAccepting(true);
    try {
      await api.post("/pharos/terms/accept", { version: terms.version });
      toast.success("Terms accepted");
      onAccepted?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to accept terms"));
    } finally {
      setAccepting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pharos terms</DialogTitle>
          <DialogDescription>
            Accept once to list a deal or request an introduction.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-gray-500">Loading terms...</p>
        ) : (
          <div className="space-y-2">
            <p className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 w-fit">
              Draft — pending legal review
            </p>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {terms?.text || "Terms are currently unavailable."}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={accepting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleAccept}
            disabled={accepting || loading || !terms}
            className="text-white"
            style={{ backgroundColor: BRAND.bg }}
            onMouseEnter={(e) => !accepting && (e.currentTarget.style.backgroundColor = BRAND.hover)}
            onMouseLeave={(e) => !accepting && (e.currentTarget.style.backgroundColor = BRAND.bg)}
          >
            {accepting ? "Accepting..." : "Accept"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
